import * as React from 'react'
import {useState} from 'react'
import {Box, Divider, Menu, MenuItem, Typography} from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import MUIModal from '../../components/MuiModal'
import UploadAgreement from './UploadAgreement'
import {formatDate} from '../../common/util'
import {useNavigate} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import {resetWizard} from '../../redux/pricingWizardSlice'
import {PANEL_GROUPS} from './merchant details/index'
import {merchantReadiness} from './shared/readiness'
import {ReadinessChip} from './shared/StateChip'
import {C, TYPE} from './shared/tokens'

// ─── Shared cell helpers ──────────────────────────────────────────────────────
export const CellLine = ({children, muted, bold, ellipsis}) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'} : {}),
    }}>
        {children}
    </Box>
)

export const CellStack = ({children}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5}}>
        {children}
    </Box>
)

// ─── Base columns (shared across all merchant views) ─────────────────────────
function buildBaseColumns(onUploadClick) {
    return [
        {
            field: 'name', headerName: 'Merchant Details',
            headerAlign: 'left', align: 'left', hideSortIcons: true, minWidth: 230,
            renderCell: ({row}) => (
                <CellStack>
                    <CellLine bold>
                        <Box
                            component='a'
                            href={`/merchant/${row.id}?openModal=true&id=${row.id}&tab=0`}
                            sx={{
                                color: 'primary.main', textDecoration: 'none', cursor: 'pointer',
                                '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
                            }}
                        >
                            {row.name || '—'}
                        </Box>
                    </CellLine>
                    {row.companyName && <CellLine muted ellipsis>{row.companyName}</CellLine>}
                    <CellLine muted>ID: {row.id}</CellLine>
                </CellStack>
            ),
        },
        {
            // Answers "which merchants aren't live yet, and why" by scanning, rather
            // than by opening each merchant and clicking through its panels. Every
            // field behind these chips is already in the searchCached projection.
            field: 'readiness', headerName: 'Setup',
            headerAlign: 'left', align: 'left', hideSortIcons: true, sortable: false,
            disableColumnMenu: true, minWidth: 190,
            renderCell: ({row}) => {
                const chips = merchantReadiness(row)
                if (!chips.length) return <CellLine muted>—</CellLine>
                const done = chips.filter((c) => c.tone === 'done').length
                return (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.4, py: 0.5}}>
                        <Box sx={{display: 'flex', gap: 0.4, flexWrap: 'wrap'}}>
                            {chips.map((c) => (
                                <ReadinessChip key={c.key} tone={c.tone} label={c.label} hint={c.hint} />
                            ))}
                        </Box>
                        <Box sx={{...TYPE.meta, color: done === chips.length ? C.ok : C.inkGhost}}>
                            {done === chips.length ? 'Ready to ship' : `${done} of ${chips.length} done`}
                        </Box>
                    </Box>
                )
            },
        },
        {
            field: 'address', headerName: 'Registered Address',
            headerAlign: 'left', align: 'left', hideSortIcons: true, minWidth: 200,
            renderCell: ({row}) => {
                const addr = [row.address, row.city, row.pincode, row.state, row.country].filter(Boolean).join(', ')
                return <CellLine muted>{addr || '—'}</CellLine>
            },
        },
        {
            field: 'createdDate', headerName: 'Created Date',
            headerAlign: 'left', align: 'left', hideSortIcons: true, minWidth: 120,
            renderCell: ({row}) => <Box sx={{fontSize: 11, color: 'text.secondary', lineHeight: 1.4}}>{formatDate(row.createdDate)}</Box>,
        },
        {
            field: 'codCycle', headerName: 'COD Cycle',
            headerAlign: 'left', align: 'left', hideSortIcons: true, minWidth: 100,
            renderCell: ({row}) => (
                <Box sx={{fontSize: '11.5px', fontWeight: 700, color: 'text.primary'}}>
                    {row?.codRemittanceConfig?.cycleOfDays ?? '—'}
                </Box>
            ),
        },
        {
            field: 'merchantSignedDoc', headerName: 'Agreement',
            headerAlign: 'left', align: 'left', hideSortIcons: true, minWidth: 110,
            renderCell: ({row}) => {
                if (row?.merchantSignedDoc?.signedDocumentUrl) {
                    return (
                        <Box
                            component='a'
                            href={row.merchantSignedDoc.signedDocumentUrl}
                            target='_blank'
                            rel='noreferrer'
                            sx={{
                                display: 'inline-flex', alignItems: 'center',
                                px: '8px', py: '3px', borderRadius: '4px',
                                fontSize: '11px', fontWeight: 600,
                                color: '#2563eb', border: '1px solid #bfdbfe', textDecoration: 'none',
                                bgcolor: 'transparent', transition: 'all 0.15s',
                                '&:hover': {bgcolor: '#eff6ff', borderColor: '#93c5fd'},
                            }}
                        >
                            View
                        </Box>
                    )
                }
                return (
                    <Box
                        component='button'
                        onClick={() => onUploadClick(row.id)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            px: '8px', py: '3px', borderRadius: '4px',
                            fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #bfdbfe',
                            transition: 'all 0.15s',
                            '&:hover': {bgcolor: '#1565c0', color: '#fff', borderColor: '#1565c0'},
                        }}
                    >
                        <UploadIcon sx={{fontSize: '13px'}} /> Upload
                    </Box>
                )
            },
        },
    ]
}

// ACTION_COLUMN is built inside the component so it can use hooks.

// ─── Reusable component ───────────────────────────────────────────────────────
/**
 * MerchantDataTable
 *
 * Renders the standard merchant table (base columns) plus any view-specific
 * extra columns injected between the base content and the Actions column.
 *
 * Props:
 *   rows          — array of merchant objects
 *   page          — current page index (0-based)
 *   setPage       — setter
 *   sizePerPage   — rows per page
 *   setSizePerPage — setter (should also reset page to 0 if needed)
 *   rowCount      — total row count for pagination
 *   extraColumns  — optional array of MUI DataGrid column defs injected before Actions
 *   reload        — numeric token; changing it triggers a parent refetch
 *   setReload     — setter passed to UploadAgreement on success
 */
export default function MerchantDataTable({
    rows = [],
    page,
    setPage,
    sizePerPage,
    setSizePerPage,
    rowCount,
    extraColumns = [],
    reload,
    setReload,
}) {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const [selectedId, setSelectedId] = useState()

    const handleClickOpen = (id) => {setOpen(true); setSelectedId(id)}
    const handleClose = () => setOpen(false)

    const [menuAnchor, setMenuAnchor] = useState(null)
    const [menuRowId, setMenuRowId] = useState(null)

    const handleMenuOpen = (event, rowId) => {
        setMenuAnchor(event.currentTarget)
        setMenuRowId(rowId)
    }
    const handleMenuClose = () => {
        setMenuAnchor(null)
        setMenuRowId(null)
    }
    const handleTabClick = (tabIndex) => {
        navigate(`/merchant/${menuRowId}?openModal=true&id=${menuRowId}&tab=${tabIndex}`)
        handleMenuClose()
    }

    const actionColumn = {
        field: 'action', headerName: '',
        headerAlign: 'right', align: 'right', hideSortIcons: true, minWidth: 360,
        renderCell: ({row}) => (
            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                <Box
                    component='a'
                    href={`/merchant/${row.id}?openModal=true&id=${row.id}`}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        fontSize: 11, fontWeight: 600, px: 1.25, py: 0.4,
                        borderRadius: 1.5, border: '1px solid', textDecoration: 'none',
                        borderColor: 'divider', color: 'text.secondary', bgcolor: 'background.paper',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    }}
                >
                    <OpenInNewIcon sx={{ fontSize: 13 }} /> View
                </Box>
                <Box
                    component='button'
                    onClick={() => { dispatch(resetWizard()); navigate(`/merchant-config/pricing-setup?merchantId=${row.id}`); }}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        fontSize: 11, fontWeight: 600, px: 1.25, py: 0.4,
                        borderRadius: 1.5, border: '1px solid', cursor: 'pointer',
                        borderColor: 'primary.main', color: 'primary.main', bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'primary.main', color: '#fff' },
                    }}
                >
                    <PriceChangeOutlinedIcon sx={{ fontSize: 13 }} /> Pricing Setup
                </Box>
                <Box
                    component='button'
                    onClick={(e) => handleMenuOpen(e, row.id)}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        fontSize: 11, fontWeight: 600, px: 1.25, py: 0.4,
                        borderRadius: 1.5, border: '1px solid', cursor: 'pointer',
                        borderColor: 'divider', color: 'text.secondary', bgcolor: 'background.paper',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    }}
                >
                    <MoreHorizIcon sx={{ fontSize: 15 }} /> More
                </Box>
            </Box>
        ),
    }

    const columns = [
        ...buildBaseColumns(handleClickOpen),
        ...extraColumns,
        actionColumn,
    ]

    return (
        <>
            <MUIModal
                open={open}
                handleClose={handleClose}
                children={
                    <UploadAgreement
                        id={selectedId}
                        setReload={setReload}
                        reload={reload}
                        setOpen={setOpen}
                    />
                }
                title='Upload Agreement'
                child={
                    <Typography variant='body2' color='red'>
                        File must be in .pdf, .png, .jpg, .jpeg format
                    </Typography>
                }
            />

            <Box sx={{bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}}>
                <ProzoDataGrid
                    autoHeight={true}
                    columns={columns}
                    getRowId={(row) => row?.id}
                    rows={rows}
                    hideFooterPagination={false}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    paginationMode='client'
                    getRowHeight={() => 'auto'}
                    rowCount={rowCount}
                />
            </Box>

            {/* ── More Actions dropdown ── */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        maxHeight: 400,
                        minWidth: 220,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        borderRadius: '8px',
                        py: 0.5,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Grouped by domain rather than one flat list of 18 -- same
                    destinations, same indices, just findable. */}
                {PANEL_GROUPS.map((group, gi) => (
                    <React.Fragment key={group.key}>
                        {gi > 0 && <Divider sx={{ my: '4px !important' }} />}
                        <Typography sx={{ ...TYPE.eyebrow, px: 2, pt: 1, pb: 0.5, display: 'block' }}>
                            {group.label}
                        </Typography>
                        {group.panels.map((panel) => (
                            <MenuItem
                                key={panel.index}
                                onClick={() => handleTabClick(panel.index)}
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    py: 0.75,
                                    px: 2,
                                    '&:hover': { bgcolor: C.accentSoft, color: C.accent },
                                }}
                            >
                                {panel.label}
                            </MenuItem>
                        ))}
                    </React.Fragment>
                ))}
            </Menu>
        </>
    )
}
